import { memo, useEffect, useMemo } from 'react';
import { FlexBox, StyledBox, StyledTypography } from './SensorAnalysis.styles';
import { NewRangeFilter } from '../SensorAnalysisView/Header/Filter/NewRangeFilter';
import AggregationOptions from './AggregationOptions';
import { DataTypeToggle } from './DataTypeToggle';
import SensorAnalysisCustomDateComponent from './SensorAnalysisCustomDateComponent';
import { useAppDispatch, useAppSelector } from 'store/hook';
import type { IGetSingleDetailTagPayload } from 'types/payloads/tag.payload.types';
import TagHandler from 'handlers/tag.handler';
import dayjs from 'dayjs';
import { setHighGrainExecute } from 'store/sensorAnalysis.slice';
import ExportSelector from 'views/FleetOverview/components/FilterViewBar/ExportSelector';
import useAppTheme from 'hooks/useAppTheme';
import { Box, type SelectChangeEvent } from '@mui/material';
import {
  getExcelExportData,
  getKey,
  getRangeDates,
} from '../SensorAnalysisView/Content/utils';
import { chartToExcelForVehicleStatus } from 'utils/exports/excel';
import useCustomerInfo from 'hooks/useCustomerInfo';
import useTimeDisplayMode from 'hooks/useTimeDisplayMode';
import { convertUtcToDisplayTime } from 'utils/helpers/dateTime';
import { DATE_TIME_FORMAT_SECONDS } from 'utils/constants/dateTime';
import { useTranslation } from 'react-i18next';

export const SensorAnalysisFilters = memo(() => {
  const { t } = useTranslation();
  const { timezoneBasedOnDisplayMode, preferredTimeDisplayMode, timezone } =
    useTimeDisplayMode();
  const { enableDarkTheme } = useAppTheme();
  const { unitSystem } = useCustomerInfo();
  const tagHandler = new TagHandler();
  const dispatch = useAppDispatch();

  const dataType = useAppSelector(
    (state) => state.sensorAnalysisReducer.dataType
  );

  const selectedAsset = useAppSelector(
    (state) => state.sensorAnalysisReducer.selectedAsset
  );

  const tagChartData = useAppSelector((state) => state.tagReducer.tagChartData);
  const tagPayload = useAppSelector((state) => state.tagReducer.tagPayload);
  const tagGrainData = useAppSelector((state) => state.tagReducer.tagGrainData);

  const selectedTags = useAppSelector(
    (state) => state.sensorAnalysisReducer.selectedTags
  );

  const activeButton = useAppSelector(
    (state) => state.sensorAnalysisReducer.regularFilters.activeButton
  );

  const grainStartDate = useAppSelector(
    (state) => state.sensorAnalysisReducer.highGrainFilters.startDateTime
  );

  const grainEndDate = useAppSelector(
    (state) => state.sensorAnalysisReducer.highGrainFilters.endDateTime
  );

  const startDate = useAppSelector(
    (state) => state.sensorAnalysisReducer.regularFilters.startDate
  );

  const endDate = useAppSelector(
    (state) => state.sensorAnalysisReducer.regularFilters.endDate
  );

  const executeGrainCall = useAppSelector(
    (state) => state.sensorAnalysisReducer.highGrainFilters.execute
  );

  const getRegularData = async (
    deviceId: string,
    payload: IGetSingleDetailTagPayload,
    tagPayload: Record<string, any>
  ) => {
    await tagHandler.getTagDetail(deviceId, payload, tagPayload, tagChartData);
  };
  const getHighGrainData = async (
    deviceId: string,
    payload: IGetSingleDetailTagPayload,
    grainStartDate: Date,
    grainEndDate: Date
  ) => {
    try {
      const updatedPayload = { ...payload, timeFrame: '30m' };
      await Promise.all([
        tagHandler.getTagGrainDetails(
          deviceId,
          updatedPayload,
          grainStartDate,
          grainEndDate
        ),
      ]);
    } catch (error) {
      console.log(error);
    } finally {
      dispatch(setHighGrainExecute(false));
    }
  };

  const keyToUse = useMemo(() => getKey(activeButton), [activeButton]);

  const exportData = async () => {
    try {
      if (selectedTags.length > 0) {
        const fileNameWithDateTime = `Sensor Analysis data for ${
          selectedAsset?.bumperNumber ?? ''
        }-${convertUtcToDisplayTime(
          new Date(),
          preferredTimeDisplayMode,
          timezone
        )}`;

        let formattedStartDate;
        let formattedEndDate;

        if (!timezoneBasedOnDisplayMode) {
          const today = new Date();
          const start = new Date(today.setHours(0, 0, 0, 0));
          const end = new Date(start);
          end.setDate(end.getDate() + 1);
          formattedStartDate = start;
          formattedEndDate = end;
        } else {
          const { startDate: rangedStartDate, endDate: rangedEndDate } =
            getRangeDates(
              activeButton,
              new Date(startDate),
              new Date(endDate),
              timezoneBasedOnDisplayMode
            );
          formattedStartDate = rangedStartDate;
          formattedEndDate = rangedEndDate;
        }

        const title = `Sensor Analysis data ${
          selectedAsset?.bumperNumber ?? ''
        } - ${preferredTimeDisplayMode} - ${timezone} ${
          activeButton === 'All'
            ? '(All records)'
            : `between ${convertUtcToDisplayTime(
                new Date(formattedStartDate),
                preferredTimeDisplayMode,
                timezone,
                DATE_TIME_FORMAT_SECONDS
              )}. and ${convertUtcToDisplayTime(
                new Date(formattedEndDate),
                preferredTimeDisplayMode,
                timezone,
                DATE_TIME_FORMAT_SECONDS
              )}.`
        }`;

        const excelData = getExcelExportData(
          selectedTags,
          tagChartData,
          keyToUse,
          activeButton,
          startDate,
          endDate,
          selectedAsset,
          unitSystem,
          dataType,
          tagGrainData,
          timezoneBasedOnDisplayMode
        );
        chartToExcelForVehicleStatus(
          excelData,
          fileNameWithDateTime,
          title,
          unitSystem,
          preferredTimeDisplayMode,
          timezone
        );
      }
    } catch (error) {
      console.error(error);
    }
  };

  const handleExportDropDownChange = async (event: SelectChangeEvent) => {
    // setExportDropdownTo(event.target.value);
    await exportData();
  };

  useEffect(() => {
    if (selectedTags.length <= 0 || !selectedAsset?.device?.deviceId) return;
    const timeFrameToUse = ['Today', '1D', '7D', '14D'].includes(activeButton)
      ? '14D'
      : activeButton === '1Y'
      ? '1Y'
      : 'ALL';
    const payload = {
      tagNames: selectedTags,
      timeFrame: timeFrameToUse,
    };
    if (dataType === 'regular') {
      void getRegularData(selectedAsset?.device?.deviceId, payload, tagPayload);
    } else {
      if (executeGrainCall && selectedAsset?.device?.deviceId) {
        void getHighGrainData(
          selectedAsset.device.deviceId,
          payload,
          dayjs(grainStartDate).toDate(),
          dayjs(grainEndDate).toDate()
        );
      }
    }
  }, [
    dataType,
    selectedTags,
    activeButton,
    grainStartDate,
    grainEndDate,
    selectedAsset,
    executeGrainCall,
  ]);

  return (
    <StyledBox sx={{ my: 2 }}>
      <StyledTypography
        variant="bodyBold"
        display="block"
        sx={{
          marginBottom: '10px',
        }}
      >
        {t('filter_raw_sensor_data')}
      </StyledTypography>
      <FlexBox sx={{ justifyContent: 'space-between' }}>
        <FlexBox sx={{ gap: 2 }}>
          {/* dataType toggle */}
          <DataTypeToggle />
          {/* quick range options */}
          {/* aggregation options - only visible when dataType is regular */}
          {dataType === 'regular' && <NewRangeFilter />}
          <SensorAnalysisCustomDateComponent />
        </FlexBox>
        <Box
          display={'flex'}
          flexDirection={'row'}
          gap={1}
          alignItems={'center'}
        >
          {dataType === 'regular' && <AggregationOptions />}
          {/* export dropdown */}
          <ExportSelector
            view={''}
            exportToLabel={t('export')}
            exportTo={''}
            onExportToChange={handleExportDropDownChange}
            enableDarkTheme={enableDarkTheme}
            hasMargin={false}
            itemArr={['Excel']}
          />
        </Box>
      </FlexBox>
    </StyledBox>
  );
});

SensorAnalysisFilters.displayName = 'SensorAnalysisFilters';
